// Javascript-Powered disclosure widget for the displaying and hiding of elements on the page, and update their corresponding button elements
// @author Jesse de Vries
//todo: expand capabilities to allow transform and fade to happen at the same time

//todo: look for nested disclosures, and those should close when their parent closes

//example of usage with custom selectors:
// new Disclosure({
//     contents: '.custom-content-selector', // Override contents selector
//     controls: '.custom-control-selector' // Override controls selector
// });

import { animationsList, handleTransitionOut, fadeIn, fadeOut, transformIn, transformOut, expandHeight, collapseHeight, expandWidth, collapseWidth }  from "./_animations.mjs"; // import the animations from our animations script so the disclosure script can use them when toggling elements on the page
import { updateButtonStates } from "./_buttons.mjs";
import { setActiveStartItems } from "./_setActiveStartItems.mjs";
import { createFromTemplate } from "./_createElements.mjs";

// Create a global list of options that we may need to update between different widgets that rely on disclosure().

    'use strict'; // enforce stricter parsing and error handling

    // Specify Console errors
    const missingAria = new TypeError("The control is missing an aria-controls attribute");
    const emptyAria = new TypeError("The provided aria-controls attribute is empty");

    // Create a variable to check against prefers-reduced-motion settings
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /**
     * Disclosure
     * @author Jesse de Vries
    */
    function Disclosure(options) {
        this.Init(options);
        this.options = options;
    }

    /**
     * Constructor
     * @Param object options
     * @return void
    */
    Disclosure.prototype = {
        // Initialize Object variables
        Init: function (options) { // Initialization

            // Default selectors
            const defaultOptions = {
                items: '[data-disclosure]',
                contents: '[data-disclosure-content]', // Toggleable elements
                controls: '[data-disclosure-control]', // Controls (buttons) used to toggle elements
                state: null
            }

            // Merge default options with provided options
            this.options = Object.assign({}, defaultOptions, options);

            // Initialize Prototype variables
            this.items = document.querySelectorAll(this.options.items);
            this.contents = document.querySelectorAll(this.options.contents);
            this.controls = document.querySelectorAll(this.options.controls);
            this.state = this.options.state;

            // Initialize Start function
            this.Start();
        },

        /**
         * Start
         * @return void
        */
        // Executes on page load
        Start: function () { // Starting functionality

            // Create local variables from the Object context
            const items = this.items;
            const controls = this.controls;
            const contents = this.contents;
            const state = this.state;

            const onLoadHandler = () => {
                setActiveStartItems(contents, this, state);
                this.UpdateButtonStates(controls);
                this.SetEvents(controls, contents, items);
            };

            if (document.readyState === 'complete') {
                // If the document is already loaded, execute immediately
                onLoadHandler();
            } else {
                // Otherwise, add a listener for the 'load' event
                window.addEventListener('load', onLoadHandler);
            }

        },

        /**
         * Update Button States
         * @return void
        */
        // Update the button states
        // todo: We might want to move this function to its own script file that deals with button-related events and import it instead. That way we can always re-use it for other scripts that rely on checking button states. In particular, tab lists use a nearly identical implementation pattern, the only difference being the use of aria-selected on the buttons instead of aria-expanded.
        UpdateButtonStates: function (controls, contentState) {
            setTimeout( () => { // We set a delay here to make sure all toggleable elements updated their states before the buttons themselves do
                updateButtonStates(controls, contentState);
            });
        },

        /**
         * SetEvents
         * @return void
        */
        // Event Handling
        SetEvents: function (controls, contents, items) {
            // todo: should this be debounced?
            controls.forEach((control) => { // Iterate through all [data-disclosure-control] elements
                const type = control.getAttribute('data-disclosure-control'); // Get the attribute value of the element, which corresponds to its type
                if (type === 'input') {
                    control.addEventListener('keydown', (e) => {
                        this.fireEvent(control, type, e);
                    });
                    control.addEventListener('focusin', (e) => {
                        this.fireEvent(control, type);
                    });
                } else if (type === 'tab') {
                    control.addEventListener('click', (e) => {
                        this.fireEvent(control, 'open', e);
                    });
                } else {
                    control.addEventListener('click', (e) => { // todo: consider using Event delegation here
                        this.fireEvent(control, type); // Jump to 'fireEvent', passing with it the instance of the button that was clicked and its type as arguments
                    });
                }
            });

            items.forEach((item) => {
                const control = document.getElementById(item.getAttribute('data-disclosure'));
                item.addEventListener('mouseenter', () => {
                    this.fireEvent(control, 'open');
                });
                item.addEventListener('mouseleave', () => {
                    this.fireEvent(control, 'close');
                });
                item.addEventListener('keydown', (e) => {
                    if (!item.contains(document.activeElement)) {
                        this.fireEvent(control, 'close');
                    }
                })
            });

            contents.forEach((content) => {
                const control = document.querySelector('[aria-controls= ' + content.getAttribute('id') + ']');
                content.addEventListener('keydown', (e) => {
                    if (e.code === 'Escape') {
                        this.fireEvent(control, 'close'); //todo: should be an option to disable this behavior
                        control.focus();
                    }
                });
                //use the HTML 'dialog' element with data-model set to true to implement native esc key behavior and trapped focus
            });

            //we are using this code for eventDelegation for dynamically-inserted content. However, we may want to attach a single event Listener on the body to control our disclosure in the future.
            const liveRegions = document.querySelectorAll('[aria-live], [role=alert i]');
            liveRegions.forEach((liveRegion) => {
                liveRegion.addEventListener('click', (e) => {
                    const target = e.target;
                    const regionControls = liveRegion.querySelectorAll('[data-disclosure-control]');
                    regionControls.forEach((control) => {
                        if (target === control) { //todo: instead of an exact match, it should check for closest().
                            const type = control.getAttribute('data-disclosure-control');
                            this.fireEvent(control, type);
                        }
                    })
                });
            });

            // New: Listen for clicks outside open disclosure content
            document.addEventListener('mousedown', (event) => {
                this.handleClickOutside(event, contents);
            });

            document.addEventListener('keydown', (event) => {
                if (event.code === 'Space' || event.code === 'Enter') { // space or enter
                    this.handleClickOutside(event, contents);
                }
            })

        },

        /**
         * Handle clicks outside open disclosure content to close it
         * @param {Event} event - The mousedown or click event
         * @param {NodeList} contents - The list of content elements
         * @return void
         */
        handleClickOutside: function (event, contents) {

            contents.forEach(content => {
                // Only act on items that are open and have data-close-on-click-outside="true"
                if (content.getAttribute('data-disclosure-content') === 'open' &&
                    content.getAttribute('data-close-on-click-outside') === 'true'
                ) {
                    // Check if the click target is outside the content element
                    if (!content.contains(event.target)) {

                        // Disable all controls that toggle this content
                        const relatedControls = document.querySelectorAll(
                            `[data-disclosure-control][aria-controls="${content.id}"]`
                        );
                        relatedControls.forEach((control) => {
                            if (event.target !== control) {
                                // Close the content
                                content.setAttribute('data-disclosure-content', 'closed');

                                // Optional: Trigger the animation to close the item if needed
                                const animation = content.getAttribute('data-animate');
                                this.SetAnimation(content, animation);

                                // Re-enable the controls after a short delay (adjust delay as needed for animation timing)
                                setTimeout(() => {
                                    relatedControls.forEach((control) => control.removeAttribute('disabled'));
                                    relatedControls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
                                }, 100); // Adjust this delay based on animation duration //((reducedMotion.matches) ? 0 : parseInt(nav.getAttribute('data-menu-duration'))))
                            }
                        });
                    }
                }
            });
        },

        /**
         * FireEvent
         * @return void
        */
        // Event handling logic
        fireEvent: function (control, type, e, controls) {

            /*if (content) {
                content.setAttribute('data-disclosure-content', 'closed');
            }*/

            if (!controls) {
                controls = this.controls;
            }

            if (control.hasAttribute('aria-controls')) {

                const targetElements = control.getAttribute('aria-controls').split(' '); // Get the space-separated values of 'aria-controls' for the clicked button, and split them into separate strings in a 'targetElements' array
                const nonMatchingIds = []; // Array to store non-matching IDs

                if (control.getAttribute('aria-controls') !== '') {

                    for (let i = 0; i < targetElements.length; i++) { // Iterate through the 'targetElements' array

                        let content = document.getElementById(targetElements[i]); // Create a variable, 'content' (same as in the 'Start' function) for each item in targetElements, and set it to be equal to the element in the document with a matching ID.

                        if (!content) {
                            // If content is not found, add the ID to nonMatchingIds array
                            nonMatchingIds.push(targetElements[i]);
                        }

                        // Check if the 'content' variable actually contains any DOM elements
                        if (content) {

                            // Create a variable where we can store the type of animation that should be used
                            let animation = content.getAttribute('data-animate');

                            if (!animation) {
                                let parentElement = content.closest('[data-animate]');
                                if (parentElement) {
                                    animation = parentElement.getAttribute('data-animate');
                                }
                            }

                            // Debounce button presses when the toggleable element is still transitioning. This makes sure button states don't update and animations don't execute in quick succession.
                            if (content.classList.contains('transitioning')) {
                                return; // todo: Potential Duplication: If the fireEvent() function is called from multiple places within the widget. We may want a more centralized debounce somewhere
                                //todo: this causes problems with quick mouse-over mouse-leaves
                            }

                            // Get the control button's [data-disclosure-control] value from a fixed set of options
                            switch (type) {
                                // If it's an 'open-only' control, change the states of the corresponding element(s) to 'open'.
                                case 'open':
                                    if (content.getAttribute('data-disclosure-content') === 'open') {
                                        return; //todo: test with more than 1 item
                                    } else {
                                        content.setAttribute('data-disclosure-content', 'open');
                                    }
                                    break;
                                // If it's a 'close-only' control, change the states of the corresponding element(s) to 'closed'.
                                case 'close':
                                    content.setAttribute('data-disclosure-content', 'closed');
                                    content.querySelectorAll('[data-disclosure-content]').forEach((el) => {
                                        if (el._transitionHandler) {
                                            el.classList.remove('active', 'transitioning');
                                            el.removeEventListener('transitionend', el._transitionHandler);
                                            delete el._transitionHandler;
                                        }
                                    });

                                    break;

                                case 'input':
                                    setTimeout(() => {
                                        if (e.code === 'Escape') {
                                            content.setAttribute('data-disclosure-content', 'close');
                                            content.classList.remove('active');
                                        }
                                        if(control.value !== '') {
                                            content.setAttribute('data-disclosure-content', 'open');
                                            content.classList.add('active');
                                        } else {
                                            content.setAttribute('data-disclosure-content', 'close');
                                            content.classList.remove('active');
                                        }
                                    })
                                    break;

                                // If it's a control that destroys the corresponding element(s) (removes them from the DOM)
                                case 'destroy':

                                    // todo: We may want to move this code into its own separate script file that deals with content mutation and import it instead, so we can re-use it for other content in case we need it
                                    const removeContent = () => {
                                        content.remove(); // remove the element from the DOM

                                        // Remove all the control buttons whose aria-controls value matches the element's id. //todo: This does not work for control buttons that control multiple elements.
                                        const relatedControls = document.querySelectorAll(
                                            `[data-disclosure-control][aria-controls="${content.id}"]`
                                        );
                                        relatedControls.forEach((relatedControl) => relatedControl.remove());
                                    };

                                    // Remove the corresponding element(s) immediately without waiting for a transition for visitors that have their motion preferences set to 'reduce', elements that are already in their 'closed' state, and items that have no/an invalid animation declared on them
                                    if (
                                        reducedMotion.matches ||
                                        !Object.values(animationsList).includes(animation) ||
                                        content.getAttribute('data-disclosure-content') === 'closed'
                                    ) {
                                        removeContent();
                                    } else {
                                        // Otherwise, wait for the animation to finish before removing the content from the DOM
                                        content.addEventListener('transitionend', () => {
                                            removeContent();
                                        });
                                    }
                                    break;

                                case 'create':

                                    const template = document.getElementById(control.getAttribute('data-template'));
                                    createFromTemplate(control.getAttribute('class'), content, template);

                                    // Todo: create a function that allows us to create new elements in the DOM...?
                                    break;
                                // Any value that is not one of the other values within this switch statement (including null/empty) will assume that the control button is a 'toggle' button
                                default:
                                    const current = content.getAttribute('data-disclosure-content') === 'open';
                                    content.setAttribute('data-disclosure-content', current ? 'closed' : 'open'); // Changing the corresponding element(s) state to the opposite of what they were when the control button was clicked

                                    if (!current) {
                                        content.querySelectorAll('[data-disclosure-content]').forEach((el) => {
                                            if (el._transitionHandler) {
                                                el.classList.remove('active', 'transitioning');
                                                el.removeEventListener('transitionend', el._transitionHandler);
                                                delete el._transitionHandler; // Clean up
                                            }
                                        });
                                    }
                                    break;
                            }

                            // If the corresponding element is not currently in a 'transitioning' state, we jump to the 'SetAnimation' function, passing with it the current element
                            if (!content.classList.contains('transitioning') && type !== 'input') { //todo: this causes problems with quick mouse-over mouse-leaves
                                this.SetAnimation(content, animation);
                            }
                        }
                        if (nonMatchingIds.length > 0) {
                            console.warn('No matching elements found in the document for the following IDs provided in "aria-controls":', nonMatchingIds);
                        }
                    }
                } else {
                    throw emptyAria; // Throw an error if an element with [data-disclosure-control] has an empty 'aria-controls' attribute, and stop executing the rest of the script
                }
            } else {
                throw missingAria; // Throw an error if an element with [data-disclosure-control] is missing an 'aria-controls' attribute, and stop executing the rest of the script
            }


            this.UpdateButtonStates(controls); // Update the control button states again once all the toggleable elements are done updating their states //todo: doesn't work when hooking into this function from tablist currently

        },

        /**
         * SetAnimation
         * @return void
        */
        // Animation handling
        SetAnimation: function (content, animation) {

            //replacement for jQuery's content.stop().hide() and content.stop().show(). We may want to test if the element is visible at all rather than test for the 'active' class and setting it, instead simply toggling display. (adding/removing display: none inline)
            const toggleContent = () => {
                if (!content.classList.contains('active')) {
                    content.classList.add('active');
                } else {
                    content.classList.remove('active');
                }
            }

            //othercontent is no longer controlled by the disclosure script. It is instead decided by the tablist, modal, navigation or accordion scripts, depending on the context. This particularly happens with the way the animations work. For example, accordion items should open/close at the same time, while active tablist items/modals should not have a fadeout animation to instantly make room for the next item.

            //todo: fade, height and width transitions currently do not work on nested content with parents that are not visible. (Their scrollHeight/scrollWidth defaults to 0px because the parent is also 0px, and 'transitionend' does not trigger because the element is not rendered when the parent is not. We should eventually fix this, but the situations where something like this occurs is pretty sparse

            // Check the visitor's motion preferences. If nothing is specified, execute the animation that is specified for the element. We need to set this check because in our CSS, we set the transition-duration to 0, which would mean that things break since 'transitionEnd' is never called. We could possibly rectify this by instead declaring a transition with a very low duration (like 1ms).
            if (!reducedMotion.matches) {
                //the duration of the animations is dictated by the animation-duration CSS property. It has a default that we can overwrite in our CSS whenever we want.

                // The animation used for toggling the element from a fixed set of options. Uses the class 'active' because it's what our _animations.mjs functions use // todo: the 'active' class indicates items that are expanded here. In _disclosure.mjs, we are using the value of [data-disclosure-content]. We should probably not rely on 2 separate values for this
                switch (animation) {

                    // Expand or collapse the element's height
                    case animationsList.toggleHeight:
                        !content.classList.contains('active') ? expandHeight(content) : collapseHeight(content);
                        break;

                    // Expand or collapse the element's width
                    case animationsList.toggleWidth:
                        !content.classList.contains('active') ? expandWidth(content) : collapseWidth(content);
                        break;

                    // Fade the element in or out
                    case animationsList.toggleFade:
                        !content.classList.contains('active') ? fadeIn(content) : fadeOut(content);
                        break;

                    case animationsList.toggleTransform:
                        !content.classList.contains('active') ? transformIn(content) : transformOut(content);
                        break;

                    // Any value that is not one of the other values within this switch statement (including null/empty) will assume that the element has no toggle animation, and apply the 'active' class to toggle the element's 'active' styles immediately.
                    // This is always the case for users that have their motion preferences set to reduced, because the check above the Switch Statement leaves the value of 'animation' as 'null'.
                    default:
                        toggleContent();
                        break;
                }

            } else {
                // I want the 'default' case in the Switch-statement above to execute for reduced-motion //todo: to make sure the animations still trigger, we want to remove this and instead make the css transition-duration very short
                toggleContent()
            }
        }

    };

    export { Disclosure };
